import React, { useState } from 'react';
import { BrowserRouter as Router, Routes, Route, useLocation } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { ThemeProvider } from './contexts/ThemeContext';
import { CategoryProvider } from './contexts/CategoryContext';
import Layout from './components/Layout';
import Home from './components/Home';
import LoginForm from './components/LoginForm';
import FamilyRegistration from './components/FamilyRegistration';
import Dashboard from './pages/Dashboard';
import Expenses from './pages/Expenses';
import IncomePage from './pages/Income';
import Users from './pages/Users';
import FamilySettings from './pages/FamilySettings';
import Categories from './pages/Categories';
import SetPassword from './pages/SetPassword';

type AuthView = 'home' | 'login' | 'register';

const AppContent: React.FC = () => {
  const { isAuthenticated, login } = useAuth();
  const [authView, setAuthView] = useState<AuthView>('home');
  const location = useLocation();

  const handleLogin = (userData: any) => {
    login(userData.user, userData.family);
  };

  const handleRegistrationSuccess = () => {
    // After successful registration, show success message
    // User will need to check email to set password
    setAuthView('home');
  };

  // Check if we're on the set-password route (public route)
  if (location.pathname.startsWith('/set-password/')) {
    return (
      <Routes>
        <Route path="/set-password/:token" element={<SetPassword />} />
      </Routes>
    );
  }

  if (!isAuthenticated) {
    switch (authView) {
      case 'login':
        return (
          <LoginForm
            onLogin={handleLogin}
            onBack={() => setAuthView('home')}
            onShowRegister={() => setAuthView('register')}
          />
        );
      case 'register':
        return (
          <FamilyRegistration
            onBack={() => setAuthView('home')}
            onSuccess={handleRegistrationSuccess}
          />
        );
      default:
        return (
          <Home
            onShowLogin={() => setAuthView('login')}
            onShowRegister={() => setAuthView('register')}
          />
        );
    }
  }

  return (
    <CategoryProvider>
      <Layout>
        <Routes>
          <Route path="/" element={<Dashboard />} />
          <Route path="/expenses" element={<Expenses />} />
          <Route path="/income" element={<IncomePage />} />
          <Route path="/users" element={<Users />} />
          <Route path="/family-settings" element={<FamilySettings />} />
          <Route path="/categories" element={<Categories />} />
        </Routes>
      </Layout>
    </CategoryProvider>
  );
};

function App() {
  return (
    <Router>
      <ThemeProvider>
        <AuthProvider>
          <AppContent />
        </AuthProvider>
      </ThemeProvider>
    </Router>
  );
}

export default App;
